import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../services/api';

const QUERY_TYPES = [
  { value: 'general_enquiry', label: 'General Enquiry' },
  { value: 'book_demo', label: 'Book a Demo' },
  { value: 'technical_support', label: 'Technical Support' },
  { value: 'sales_pricing', label: 'Sales & Pricing' },
];

const CUSTOMER_SEGMENTS = [
  'Individual / Family',
  'Friends Group',
  'Event Organiser',
  'Photographer / Videographer',
  'Corporate / Organisation',
  'Other',
];

const initialState = {
  full_name: '',
  usage_type: 'Personal',
  email: '',
  phone_number: '',
  customer_segment: '',
  query_type: 'general_enquiry',
  message: '',
};

export default function ContactUsDialog({ open, onClose }) {
  const [form, setForm] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setForm(initialState);
    setError('');
    setSuccess(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill in your name, email, and message.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      await api.submitContactForm(form);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to send your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Contact Us
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            Thanks for reaching out! We've received your message and will get back to you soon.
          </Alert>
        ) : (
          <Box component="form" id="contact-us-form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TextField
              label="Full Name"
              fullWidth
              required
              margin="normal"
              value={form.full_name}
              onChange={handleChange('full_name')}
            />

            <FormControl component="fieldset" sx={{ mt: 1, mb: 1 }}>
              <FormLabel component="legend">Personal or Commercial use</FormLabel>
              <RadioGroup row value={form.usage_type} onChange={handleChange('usage_type')}>
                <FormControlLabel value="Personal" control={<Radio />} label="Personal" />
                <FormControlLabel value="Commercial" control={<Radio />} label="Commercial" />
              </RadioGroup>
            </FormControl>

            <TextField
              label="Email ID"
              type="email"
              fullWidth
              required
              margin="normal"
              value={form.email}
              onChange={handleChange('email')}
            />

            <TextField
              label="Phone Number"
              type="tel"
              fullWidth
              margin="normal"
              value={form.phone_number}
              onChange={handleChange('phone_number')}
            />

            <TextField
              select
              label="Customer Segment"
              fullWidth
              margin="normal"
              value={form.customer_segment}
              onChange={handleChange('customer_segment')}
            >
              {CUSTOMER_SEGMENTS.map((segment) => (
                <MenuItem key={segment} value={segment}>{segment}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Query Type"
              fullWidth
              required
              margin="normal"
              value={form.query_type}
              onChange={handleChange('query_type')}
            >
              {QUERY_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </TextField>

            <TextField
              label="Message"
              fullWidth
              required
              multiline
              rows={4}
              margin="normal"
              value={form.message}
              onChange={handleChange('message')}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {success ? (
          <Button onClick={handleClose} variant="contained" sx={{ textTransform: 'none' }}>
            Close
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={isSubmitting} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="contact-us-form"
              variant="contained"
              disabled={isSubmitting}
              sx={{ textTransform: 'none' }}
            >
              {isSubmitting ? <CircularProgress size={18} color="inherit" /> : 'Send Message'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
